import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDown } from 'lucide-react';
import { cva, VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { OmitStylesComponentProps } from '../../types';

const selectTriggerVariants = cva(
    'rocco-select-trigger flex w-full items-center justify-between rounded-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
    {
        variants: {
            variant: {
                default: 'px-4 py-3',
                small: 'p-3',
                medium: 'px-6 py-3',
                wide: 'px-9 py-3',
                large: 'px-8 py-4',
            },
            text: {
                default: 'text-[16px] leading-none md:text-base',
                small: 'text-3.5 leading-none',
                medium: 'text-[16px] font-medium leading-none file:font-medium data-[placeholder]:font-medium md:text-base',
            },
            color: {
                default:
                    'border border-gray-400 bg-black/5 text-gray-900 data-[placeholder]:text-gray-400',
                lightOutline:
                    'border border-gray-200 bg-white text-gray-900 data-[placeholder]:text-gray-500/70',
                gray: 'border border-gray-100 bg-gray-100 text-gray-900 data-[placeholder]:text-gray-900/50',
                transparent:
                    'border-none bg-transparent text-gray-900 data-[placeholder]:text-gray-900/50',
            },
            focusStyle: {
                default: 'focus:ring-2 focus:ring-ring',
                none: 'focus:ring-0 focus:ring-transparent focus:ring-offset-0',
            },
        },
        defaultVariants: {
            variant: 'default',
            text: 'default',
            color: 'default',
            focusStyle: 'default',
        },
    }
);

export const SelectTrigger = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    OmitStylesComponentProps<typeof SelectPrimitive.Trigger> &
        VariantProps<typeof selectTriggerVariants> & {
            className?: string;
            hideIcon?: boolean;
        }
>(
    (
        {
            children,
            className,
            variant,
            text,
            color,
            focusStyle,
            hideIcon = false,
            ...props
        },
        ref
    ) => (
        <SelectPrimitive.Trigger
            ref={ref}
            className={cn(
                selectTriggerVariants({
                    variant,
                    text,
                    color,
                    focusStyle,
                }),
                className
            )}
            {...props}
        >
            {children}
            {!hideIcon && (
                <SelectPrimitive.Icon asChild>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                </SelectPrimitive.Icon>
            )}
        </SelectPrimitive.Trigger>
    )
);

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
